"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Edge() {
  const features = [{
    number: "01",
    heading: "AI-Driven Threat Detection",
    body: "Our advanced AI engines analyze network traffic in real-time, identifying anomalies and potential breach attempts before they impact your operations. We provide the intelligence needed to stay ahead of sophisticated cyber adversaries.",
    imageLeft: true,
    ratio: "7-5",
    visual: "network"
  }, {
    number: "02",
    heading: "Proactive Risk Management",
    body: "We continuously assess your digital infrastructure to identify vulnerabilities. By mapping your attack surface, we help you implement robust defenses that evolve alongside the changing threat landscape.",
    imageLeft: false,
    ratio: "5-7",
    visual: "chart"
  }, {
    number: "03",
    heading: "Rapid Incident Response",
    body: "When threats emerge, our expert team and automated systems act instantly to contain and neutralize risks. We ensure your business continuity with precision-engineered security protocols.",
    imageLeft: true,
    ratio: "7-5",
    visual: "layers"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4038" id="the-ai-edge" className="bg-background text-foreground py-32 md:py-44 lg:py-[180px] px-6 md:px-10 lg:px-16 relative overflow-hidden">
      {}
      <div ref={headerRef} className={`max-w-7xl mx-auto mb-24 md:mb-32 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="text-left mb-10">
          <span className="font-mono text-xs md:text-sm tracking-widest uppercase text-primary">
            03 / THE EDGE
          </span>
        </div>
        <h2 className="font-default font-bold text-center mx-auto leading-[1.05] tracking-tight max-w-5xl" style={{
        fontSize: "clamp(3rem, 6vw, 5rem)"
      }}>
          What <span className="text-primary">separates InfinitySecure</span> from standard defense.
        </h2>
        <div className="flex justify-center mt-12">
          <div className="h-px w-20 bg-primary" />
        </div>
      </div>

      {}
      <div className="max-w-7xl mx-auto">
        {features.map((feature, i) => {
        const {
          ref,
          isVisible
        } = useRevealOnScroll<HTMLDivElement>();
        return <div key={i} data-index={i}>
              <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center py-20 md:py-28 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                {}
                <div className={`${feature.imageLeft ? "lg:col-span-7 lg:order-1" : "lg:col-span-7 lg:order-2"}`}>
                  <div className="relative bg-card/40 backdrop-blur-md border border-border shadow-2xl aspect-[4/3] overflow-hidden" style={{
                borderRadius: "0px"
              }}>
                    {}
                    <div className="absolute top-0 left-0 h-px w-24 bg-primary" />
                    <div className="absolute bottom-6 right-6 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
                      SECURE / DATA-{feature.number}
                    </div>

                    {}
                    {feature.visual === "network" && <svg viewBox="0 0 400 300" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                        <circle cx="200" cy="150" r="80" fill="none" stroke="var(--primary)" strokeWidth="0.5" opacity="0.4" />
                        <circle cx="200" cy="150" r="120" fill="none" stroke="var(--primary)" strokeWidth="0.5" opacity="0.25" />
                        <circle cx="200" cy="150" r="40" fill="none" stroke="var(--primary)" strokeWidth="0.5" opacity="0.6" />
                        {[[200, 70], [280, 110], [310, 180], [260, 240], [160, 250], [100, 200], [90, 120], [150, 70]].map(([x, y], idx) => <g key={idx}>
                            <line x1="200" y1="150" x2={x} y2={y} stroke="var(--primary)" strokeWidth="0.5" opacity="0.5" />
                            <circle cx={x} cy={y} r="3" fill="var(--primary)" />
                          </g>)}
                        <circle cx="200" cy="150" r="5" fill="var(--primary)" />
                        <circle cx="200" cy="150" r="10" fill="none" stroke="var(--primary)" strokeWidth="1">
                          <animate attributeName="r" from="5" to="40" dur="2s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="2s" repeatCount="indefinite" />
                        </circle>
                      </svg>}

                    {feature.visual === "chart" && <svg viewBox="0 0 400 300" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                        {[60, 120, 180, 240].map(y => <line key={y} x1="40" y1={y} x2="380" y2={y} stroke="var(--border)" strokeWidth="0.5" />)}
                        <polyline points="40,220 90,200 140,210 190,160 240,140 290,100 340,80 380,50" fill="none" stroke="var(--primary)" strokeWidth="1.5" />
                        <polyline points="40,240 90,230 140,235 190,200 240,180 290,150 340,130 380,110" fill="none" stroke="var(--primary)" strokeWidth="0.5" strokeDasharray="3 3" opacity="0.5" />
                        {[[40, 220], [90, 200], [140, 210], [190, 160], [240, 140], [290, 100], [340, 80], [380, 50]].map(([x, y], idx) => <circle key={idx} cx={x} cy={y} r="2" fill="var(--primary)" />)}
                      </svg>}

                    {feature.visual === "layers" && <svg viewBox="0 0 400 300" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                        {[0, 1, 2, 3].map(idx => <rect key={idx} x={80 + idx * 20} y={60 + idx * 25} width="180" height="120" fill="none" stroke="var(--primary)" strokeWidth="0.8" opacity={1 - idx * 0.2} />)}
                        <line x1="80" y1="60" x2="140" y2="60" stroke="var(--primary)" strokeWidth="1.5" />
                        <circle cx="80" cy="60" r="3" fill="var(--primary)" />
                      </svg>}
                  </div>
                </div>

                {}
                <div className={`relative ${feature.imageLeft ? "lg:col-span-5 lg:order-2" : "lg:col-span-5 lg:order-1"}`}>
                  {}
                  <span className="font-serif italic absolute -top-8 -left-2 lg:-left-4 pointer-events-none select-none text-primary" style={{
                fontSize: "8rem",
                lineHeight: 1,
                opacity: 0.15,
                zIndex: 0
              }} aria-hidden="true">
                    {feature.number}
                  </span>
                  <div className="relative z-10">
                    <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-[2.5rem] leading-tight tracking-tight mb-6">
                      {feature.heading}
                    </h3>
                    <p className="font-default text-foreground/85 mb-8" style={{
                  fontSize: "1.125rem",
                  lineHeight: 1.7
                }}>
                      {feature.body}
                    </p>
                    <Button asChild variant="link" className="text-primary hover:text-primary/80 px-0 font-mono text-sm tracking-widest uppercase">
                      <Link to="/ExpertisePage">
                        Learn more
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {}
              {i < features.length - 1 && <div className="h-px w-full bg-border" />}
            </div>;
      })}
      </div>
    </section>;
}